import { useEffect, useState } from 'react';
import { useApiRequest } from './useApiRequest';

/**
 * What the page needs to render an image that only an authenticated caller may fetch.
 *
 * `objectUrl` is an in-memory `blob:` URL, never a link to the API. It is created when the
 * bytes arrive and revoked when it is replaced, when the component unmounts, and when the
 * fetch fails — so a portrait never outlives the screen that showed it.
 */
export interface AuthenticatedImage {
    /** In-memory object URL, or null while loading, when absent, or after a failure. */
    objectUrl: string | null;
    /** True once the fetch has failed, so the caller can show a placeholder instead. */
    failed: boolean;
}

/**
 * Fetches an authenticated image and exposes it as an object URL.
 *
 * Client portraits are personal data. They were previously served by an unauthenticated static
 * mount, which let `<img src>` render them directly; now the request carries a bearer token, so
 * the bytes have to be fetched by script and handed to the browser as an object URL.
 *
 * Nothing is written to `localStorage`, `sessionStorage`, IndexedDB or a service worker: the
 * only copy is the Blob the object URL points at, and revoking the URL releases it.
 *
 * @param path API path of the image, or null/undefined when there is nothing to show.
 */
export function useAuthenticatedImage(path?: string | null): AuthenticatedImage {
    const { makeBlobRequest } = useApiRequest();
    const [objectUrl, setObjectUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setObjectUrl(null);
        setFailed(false);

        if (!path) {
            return;
        }

        // `cancelled` guards the state writes; `created` is what the cleanup revokes. Both are
        // per-effect, so a second path arriving mid-flight cannot revoke the newer URL.
        let cancelled = false;
        let created: string | null = null;

        void makeBlobRequest(path)
            .then(blob => {
                if (cancelled) {
                    // The effect was torn down before the bytes arrived; no URL is created, so
                    // there is nothing to revoke and nothing to leak.
                    return;
                }
                created = URL.createObjectURL(blob);
                setObjectUrl(created);
            })
            .catch(() => {
                if (!cancelled) {
                    setFailed(true);
                }
            });

        return () => {
            cancelled = true;
            if (created !== null) {
                URL.revokeObjectURL(created);
            }
        };
    }, [path, makeBlobRequest]);

    return { objectUrl, failed };
}
